import { ApiService } from '@/services/core/ApiService'
import type { ApiResponse } from '@/types/core/api'
import type { CartItem } from '@/stores/cartStore'
import { logger } from '@/utils/logger'

// 購物車 API 請求介面
export interface CartItemRequest {
  productId: string
  quantity: number
}

export interface UpdateCartItemRequest {
  quantity: number
}

// 購物車 API 回應介面
export interface CartResponse {
  cartId: number
  userId: string
  channelId: string
  items: CartItemResponse[]
  createdAt: string
  updatedAt: string
}

export interface CartItemResponse {
  itemId: number
  cartId: number
  productId: number
  quantity: number
  product?: {
    uid: string
    name: string
    description: string
    points: number
    unlimitedPoints: boolean
    mainImageUrl?: string
    vendorId: string
    vendorName: string
    status: string
  }
  createdAt: string
  updatedAt: string
}

/**
 * 購物車 API 服務
 * 負責與後端購物車 API 的通信
 */
class CartService extends ApiService {
  constructor() {
    super('/api')
  }

  /**
   * 獲取用戶購物車
   */
  async getCart(): Promise<ApiResponse<CartResponse>> {
    return this.get('/cart')
  }

  /**
   * 添加商品到購物車
   */
  async addToCart(productId: string, quantity: number = 1): Promise<ApiResponse<CartItemResponse>> {
    const request: CartItemRequest = { productId, quantity }
    return this.post('/cart/items', request)
  }

  /**
   * 更新購物車商品數量
   */
  async updateCartItem(cartItemId: string, quantity: number): Promise<ApiResponse<CartItemResponse>> {
    const request: UpdateCartItemRequest = { quantity }
    return this.put(`/cart/items/${cartItemId}`, request)
  }

  /**
   * 從購物車移除商品
   */
  async removeFromCart(cartItemId: string): Promise<ApiResponse<void>> {
    return this.delete(`/cart/items/${cartItemId}`)
  }

  /**
   * 清空購物車
   */
  async clearCart(): Promise<ApiResponse<void>> {
    return this.delete('/cart')
  }

  /**
   * 移除特定店家的所有商品
   */
  async removeVendorItems(vendorId: string): Promise<ApiResponse<void>> {
    return this.delete(`/cart/vendor/${vendorId}`)
  }

  /**
   * 同步本地購物車到伺服器
   * 用於登錄後將本地資料上傳到伺服器
   */
  async syncLocalCart(localItems: CartItem[]): Promise<ApiResponse<CartResponse>> {
    const syncData = localItems.map(item => ({
      productId: item.productId,
      quantity: item.quantity
    }))
    
    logger.info('同步本地購物車到伺服器', { itemCount: syncData.length })
    return this.post('/cart/sync', { items: syncData })
  }

  /**
   * 批量更新購物車
   * 用於一次性更新多個商品的數量
   */
  async batchUpdateCart(updates: Array<{ productId: string; quantity: number }>): Promise<ApiResponse<CartResponse>> {
    return this.put('/cart/batch', { items: updates })
  }
}

// 導出單例實例
export const cartService = new CartService()